import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import { 
  User, FileText, MessageSquare, Receipt, 
  CheckCircle, ArrowRight, Loader2, Eye, EyeOff,
  Users, ClipboardCheck, Send
} from 'lucide-react';

const API_URL = window.location.origin;

const CoachPortalPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please enter your email and password');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        email,
        password
      });

      const { token, user } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));

      // Check if user is a coach
      if (user.is_coach || user.role === 'admin' || user.role === 'super_admin') {
        toast.success('Welcome to the Coach Portal!');
        // Use window.location for a full page navigation to ensure auth context is refreshed
        window.location.href = '/coach';
      } else {
        toast.error('This portal is for coaches only. Please use the main login.');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: User,
      title: 'Manage Your Profile',
      description: 'Keep your coach profile updated with your latest credentials, specialties, and bio.'
    },
    {
      icon: Users,
      title: 'View Your Clients',
      description: 'Access information about your assigned clients and track your coaching relationships.'
    },
    {
      icon: MessageSquare,
      title: 'Message the Team',
      description: 'Communicate directly with the Volition team through the built-in messaging system.'
    },
    {
      icon: Receipt,
      title: 'Submit Invoices',
      description: 'Submit and track your coaching invoices all in one place.'
    },
    {
      icon: FileText,
      title: 'Agreements',
      description: 'Review and sign coaching agreements electronically.'
    },
    {
      icon: ClipboardCheck,
      title: 'Session Notes',
      description: 'Document session notes and track client progress.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-cream to-white">
      {/* Header */}
      <div className="bg-forest text-white py-4 px-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-heading font-bold text-gold">Volition Enterprises</h1>
            <p className="text-sm text-white/80">Sister Diamonds Coach Portal</p>
          </div>
          <Button 
            variant="outline" 
            className="text-white border-white/30 hover:bg-white/10"
            onClick={() => navigate('/')}
          >
            Main Site
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-2 gap-12 items-start">
          {/* Left Column - Information */}
          <div>
            <h2 className="text-3xl font-heading font-bold text-forest mb-4">
              Welcome to the Coach Portal
            </h2>
            <p className="text-gray-600 mb-8 text-lg">
              The central hub for Volition coaches to manage their profiles, communicate with our team, 
              and streamline their coaching practice.
            </p>

            <div className="space-y-4">
              {features.map((feature, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-forest/10 flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-5 h-5 text-forest" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-forest">{feature.title}</h3>
                    <p className="text-sm text-gray-500">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 p-4 bg-gold/10 rounded-lg border border-gold/20">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-gold mt-0.5" />
                <div>
                  <p className="font-medium text-forest">Need Help?</p>
                  <p className="text-sm text-gray-600">
                    If you have questions or need assistance accessing your account, 
                    please contact the Volition team at{' '}
                    <a href="mailto:info@volitionenterprises.com" className="text-forest underline">
                      info@volitionenterprises.com
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Login Form */}
          <div>
            <Card className="shadow-lg">
              <CardHeader className="text-center pb-2">
                <CardTitle className="text-2xl text-forest">Coach Login</CardTitle>
                <CardDescription>
                  Enter your credentials to access the portal
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="h-11"
                      data-testid="coach-login-email"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="h-11 pr-10"
                        data-testid="coach-login-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full h-11 bg-forest hover:bg-forest/90 text-white"
                    disabled={loading}
                    data-testid="coach-login-submit"
                  >
                    {loading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Signing In...</>
                    ) : (
                      <>Access Portal <ArrowRight className="w-4 h-4 ml-2" /></>
                    )}
                  </Button>

                  <div className="text-center pt-2">
                    <a 
                      href="/reset-password" 
                      className="text-sm text-forest hover:underline"
                    >
                      Forgot your password?
                    </a>
                  </div>
                </form>

                <div className="mt-6 pt-6 border-t text-center">
                  <p className="text-sm text-gray-500">
                    Not a coach?{' '}
                    <a href="/login" className="text-forest hover:underline font-medium">
                      Member Login
                    </a>
                  </p>
                </div>
              </CardContent>
            </Card>

            <div className="mt-6 text-center">
              <p className="text-xs text-gray-400">
                Volition Enterprises | Sister Diamonds Coaching Platform
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoachPortalPage;
